// v2.19.35 — Dashboard commerçant /ops/commandes : liste live des livraisons +
// modal "+ Nouvelle livraison" qui POST /api/v3/delivery/dispatch puis ouvre
// les 2 wa_url dans des onglets.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "civ_user_token";

function _authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_LABELS = {
  pending_match:      { l: "🔎 Recherche livreur", c: "#94A3B8" },
  pending_acceptance: { l: "⏳ En attente acceptation", c: "#FBBF24" },
  accepted:           { l: "🚖 Livreur en route pickup", c: "#60A5FA" },
  picked_up:          { l: "📦 Colis pris", c: "#A78BFA" },
  delivering:         { l: "🛵 En livraison", c: "#A78BFA" },
  delivered:          { l: "✅ Livré", c: "#22C55E" },
  cancelled:          { l: "🚫 Annulé", c: "#94A3B8" },
  failed:             { l: "❌ Échec", c: "#F87171" },
};

export default function OpsCommandesPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/v3/delivery/my-orders`, { headers: _authHeaders() });
      const d = await r.json();
      if (r.ok && d.ok) setOrders(d.orders || []);
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 10000); // poll 10s
    return () => clearInterval(timerRef.current);
  }, [load]);

  const grouped = useMemo(() => {
    const buckets = { active: [], pending: [], done: [] };
    for (const o of orders) {
      if (['delivered', 'cancelled', 'failed'].includes(o.status)) buckets.done.push(o);
      else if (['accepted', 'picked_up', 'delivering'].includes(o.status)) buckets.active.push(o);
      else buckets.pending.push(o);
    }
    return buckets;
  }, [orders]);

  return (
    <div data-testid="ops-commandes-page" style={pageStyle}>
      <a href="/" data-testid="ops-back-link" style={backLinkStyle}>← Retour au chat</a>
      <header style={headerStyle}>
        <h1 style={{ margin: 0, fontSize: 24, color: "#FBBF24" }}>📦 Mes commandes</h1>
        <button onClick={() => setModalOpen(true)} data-testid="dispatch-open-btn" style={primaryBtnStyle}>
          ➕ Nouvelle livraison
        </button>
      </header>

      {loading && orders.length === 0 && <div style={{ color: "#94A3B8" }}>Chargement…</div>}

      <Section title={`⏳ En attente (${grouped.pending.length})`} orders={grouped.pending} />
      <Section title={`🚖 En cours (${grouped.active.length})`} orders={grouped.active} />
      <Section title={`📜 Terminées (${grouped.done.length})`} orders={grouped.done} collapsed />

      {modalOpen && <DispatchModal onClose={() => setModalOpen(false)} onSuccess={() => { setModalOpen(false); load(); }} />}
    </div>
  );
}

function Section({ title, orders, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <section style={{ marginBottom: 20 }}>
      <button onClick={() => setOpen((v) => !v)} style={sectionTitleStyle}>{open ? "▾" : "▸"} {title}</button>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          {orders.length === 0 && <div style={{ color: "#64748b", fontSize: 13 }}>Aucune commande.</div>}
          {orders.map((o) => <OrderCard key={o.order_ref} o={o} />)}
        </div>
      )}
    </section>
  );
}

function OrderCard({ o }) {
  const st = STATUS_LABELS[o.status] || { l: o.status, c: "#94A3B8" };
  const trackUrl = `/track/${o.order_ref}`;
  return (
    <div data-testid={`order-card-${o.order_ref}`} style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "monospace", color: "#93C5FD", fontSize: 13, fontWeight: 700 }}>{o.order_ref}</div>
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>
            📦 {o.commerce_name} → 📍 {o.drop?.description || o.drop?.address_civ || "—"}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {o.items_summary} · {o.distance_km} km · 💰 {o.delivery_fee_fcfa} FCFA
            {o.drop?.client_pin && <> · 🔑 PIN <code style={{ color: "#FBBF24" }}>{o.drop.client_pin}</code></>}
          </div>
        </div>
        <span style={{ ...badgeStyle, color: st.c, borderColor: st.c }}>{st.l}</span>
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <a href={trackUrl} target="_blank" rel="noopener noreferrer" data-testid={`order-track-${o.order_ref}`} style={miniBtnStyle}>🔗 Suivi</a>
        <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}${trackUrl}`).then(() => toast.success("Lien copié"))} style={miniBtnStyle}>📋 Copier le lien</button>
      </div>
    </div>
  );
}

function DispatchModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    client_phone: "", client_name: "", commerce_name: "Pharmacie Cocody Test",
    pickup_lat: "5.3463", pickup_lng: "-3.9929", pickup_desc: "Mon commerce",
    drop_lat: "", drop_lng: "", drop_desc: "",
    items_summary: "1 colis", estimated_value_fcfa: "", urgency: "normal",
  });
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchQ, setSearchQ] = useState("");

  const onSearchDrop = async (q) => {
    setSearchQ(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await fetch(`${API}/v3/addresses/search?q=${encodeURIComponent(q)}&limit=5`);
      const d = await r.json();
      if (d.ok) setResults(d.results || []);
    } catch (_) {}
    setSearching(false);
  };

  const pickResult = (res) => {
    setForm((f) => ({ ...f, drop_desc: res.friendly_name + " (" + res.commune_name + ")", drop_address_civ: res.address_civ }));
    setSearchQ(res.friendly_name);
    setResults([]);
    // On a besoin des lat/lng → fetch by-slug pour les récupérer.
    fetch(`${API}/v3/addresses/by-slug/${res.friendly_name_slug}-${res.address_civ.split("-").pop().toLowerCase()}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.ok && d.claim) setForm((f) => ({ ...f, drop_lat: String(d.claim.lat), drop_lng: String(d.claim.lng) }));
      });
  };

  const submit = async () => {
    if (!form.client_phone || !form.drop_lat || !form.drop_lng) {
      toast.error("Téléphone client + drop GPS requis");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_phone: form.client_phone,
        client_name: form.client_name,
        commerce_name: form.commerce_name,
        pickup: { lat: Number(form.pickup_lat), lng: Number(form.pickup_lng), description: form.pickup_desc },
        drop: { lat: Number(form.drop_lat), lng: Number(form.drop_lng), description: form.drop_desc, address_civ: form.drop_address_civ || null },
        items_summary: form.items_summary,
        estimated_value_fcfa: form.estimated_value_fcfa ? Number(form.estimated_value_fcfa) : null,
        urgency: form.urgency,
      };
      const r = await fetch(`${API}/v3/delivery/dispatch`, {
        method: "POST", headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        toast.success(`✅ ${d.order_ref} créée (${d.candidates_count} candidat${d.candidates_count > 1 ? "s" : ""})`);
        // Ouvre les 2 wa_url
        if (d.wa_commerce_url) window.open(d.wa_commerce_url, "_blank");
        if (d.wa_courier_url) setTimeout(() => window.open(d.wa_courier_url, "_blank"), 500);
        onSuccess();
      } else if (d.status === "failed") {
        toast.error("Aucun livreur dispo. Réessaye plus tard.");
      } else {
        toast.error(d.error || `Erreur ${r.status}`);
      }
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setSubmitting(false); }
  };

  return (
    <div onClick={onClose} data-testid="dispatch-modal-overlay" style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} data-testid="dispatch-modal" style={modalStyle}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, color: "#FBBF24", fontWeight: 700 }}>
          <span>➕ Nouvelle livraison</span>
          <button onClick={onClose} style={closeBtnStyle}>×</button>
        </header>
        <Field label="📞 Téléphone client" v={form.client_phone} onC={(v) => setForm({ ...form, client_phone: v })} placeholder="+2250507080910" testid="dispatch-client-phone" />
        <Field label="👤 Nom client (optionnel)" v={form.client_name} onC={(v) => setForm({ ...form, client_name: v })} placeholder="Marie K." testid="dispatch-client-name" />
        <div style={{ marginBottom: 10 }}>
          <label style={lblStyle}>📍 Drop (adresse de livraison)</label>
          <input type="text" value={searchQ} onChange={(e) => onSearchDrop(e.target.value)} placeholder='Cherche "Maison Koffi"…' data-testid="dispatch-drop-search" style={inputStyle} />
          {searching && <div style={{ fontSize: 11, color: "#94A3B8" }}>Recherche…</div>}
          {results.length > 0 && (
            <div style={{ background: "rgba(15,23,42,0.95)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, marginTop: 4, maxHeight: 160, overflowY: "auto" }}>
              {results.map((r) => (
                <button key={r.address_civ} onClick={() => pickResult(r)} data-testid={`dispatch-drop-result-${r.address_civ}`} style={resultBtnStyle}>
                  <div style={{ fontWeight: 700, color: "#FBBF24", fontSize: 13 }}>{r.friendly_name}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: "#93C5FD" }}>{r.address_civ}</div>
                  <div style={{ fontSize: 11, color: "#94A3B8" }}>📍 {r.commune_name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          <Field label="Lat" v={form.drop_lat} onC={(v) => setForm({ ...form, drop_lat: v })} testid="dispatch-drop-lat" />
          <Field label="Lng" v={form.drop_lng} onC={(v) => setForm({ ...form, drop_lng: v })} testid="dispatch-drop-lng" />
        </div>
        <Field label="📦 Contenu (court)" v={form.items_summary} onC={(v) => setForm({ ...form, items_summary: v })} testid="dispatch-items" />
        <Field label="💵 Valeur estimée (FCFA, optionnel)" v={form.estimated_value_fcfa} onC={(v) => setForm({ ...form, estimated_value_fcfa: v })} testid="dispatch-value" />
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {["normal", "express"].map((u) => (
            <button key={u} onClick={() => setForm({ ...form, urgency: u })} data-testid={`dispatch-urgency-${u}`} style={{ ...chipStyle, background: form.urgency === u ? "#FBBF24" : "rgba(255,255,255,0.06)", color: form.urgency === u ? "#0F172A" : "#E8ECF7" }}>
              {u === "express" ? "⚡ Express" : "🐢 Normal"}
            </button>
          ))}
        </div>
        <button onClick={submit} disabled={submitting} data-testid="dispatch-submit" style={{ ...primaryBtnStyle, width: "100%", opacity: submitting ? 0.5 : 1 }}>
          {submitting ? "Envoi…" : "🚖 Demander un livreur"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, onC, placeholder, testid }) {
  return (
    <div style={{ flex: 1, marginBottom: 10 }}>
      <label style={lblStyle}>{label}</label>
      <input type="text" value={v} onChange={(e) => onC(e.target.value)} placeholder={placeholder || ""} data-testid={testid} style={inputStyle} />
    </div>
  );
}

const pageStyle = { minHeight: "100vh", padding: "20px 16px", background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)", color: "#E8ECF7", fontFamily: "system-ui, sans-serif" };
const backLinkStyle = { color: "#FBBF24", textDecoration: "none", fontSize: 13, display: "inline-block", marginBottom: 12 };
const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22, gap: 10, flexWrap: "wrap" };
const sectionTitleStyle = { background: "none", border: "none", color: "#E8ECF7", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: 0 };
const cardStyle = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12, padding: 14 };
const badgeStyle = { fontSize: 11, padding: "3px 10px", borderRadius: 999, background: "rgba(255,255,255,0.04)", fontWeight: 700, border: "1px solid", whiteSpace: "nowrap" };
const miniBtnStyle = { background: "rgba(255,255,255,0.06)", color: "#E8ECF7", border: "1px solid rgba(255,255,255,0.12)", padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", textDecoration: "none" };
const primaryBtnStyle = { background: "linear-gradient(135deg, #16A34A, #22C55E)", color: "#0F172A", border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.35)" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, padding: 14, overflowY: "auto" };
const modalStyle = { background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)", color: "#E8ECF7", borderRadius: 16, padding: 22, maxWidth: 460, width: "100%", marginTop: 30, marginBottom: 30, border: "1px solid rgba(255,255,255,0.10)", boxShadow: "0 10px 40px rgba(0,0,0,0.6)" };
const closeBtnStyle = { background: "none", border: "none", color: "#94A3B8", fontSize: 26, cursor: "pointer" };
const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontSize: 13, outline: "none", boxSizing: "border-box" };
const lblStyle = { display: "block", fontSize: 12, color: "#94A3B8", marginBottom: 4 };
const chipStyle = { padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const resultBtnStyle = { display: "block", width: "100%", background: "none", border: "none", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: 10, textAlign: "left", cursor: "pointer" };
